/* @jsx h */
import { defineComponent, h, ref, computed, watch } from "vue";
import { RightSheet, useLocalization } from "@lemmonhq/uikit";
import { getRestaurantDateKey } from "@lemmonhq/blogic";
import {
  RestaurantSettingKey,
  timeToMinutes,
  type VacationScheduleItem,
} from "@lemmonhq/types";
import { api } from "@app/data/api";
import { useAuth } from "@app/vue/providers/Auth";
import { useToast } from "@app/vue/providers/Toast";

export interface ScheduledClosuresSheetProps {
  isOpen: boolean;
  onClose: () => void;
}

// A pre-planned closure entry in the vacation schedule: either the whole day
// (`closed_all_day`) or a blocked time range (`closed_range`).
const CLOSURE_REASON = "closed_by_manager";

type ClosureEntry = {
  date: string;
  // Both set for a time-range block, both undefined for an all-day closure.
  start?: string;
  end?: string;
  guestNote?: string;
};

const entryKey = (entry: ClosureEntry) =>
  `${entry.date}|${entry.start ?? "all"}|${entry.end ?? ""}`;

// Does a stored vacation item represent this list entry?
const matchesEntry = (item: any, entry: ClosureEntry) => {
  if (item?.date !== entry.date || item?.reason !== CLOSURE_REASON) {
    return false;
  }
  if (entry.start === undefined) {
    return item.payload?.type === "closed_all_day";
  }
  return (
    item.payload?.type === "closed_range" &&
    item.payload.start === entry.start &&
    item.payload.end === entry.end
  );
};

// "00:00" as an end time means midnight; otherwise the end must be later.
const isValidRange = (start: string, end: string) => {
  const s = timeToMinutes(start);
  const e = timeToMinutes(end);
  if (Number.isNaN(s) || Number.isNaN(e)) return false;
  return e === 0 ? s > 0 : s < e;
};

export const ScheduledClosuresSheet =
  defineComponent<ScheduledClosuresSheetProps>({
    name: "ScheduledClosuresSheet",
    props: ["isOpen", "onClose"],
    setup(props) {
      const { t } = useLocalization();
      const auth = useAuth();
      const toast = useToast();

      const timezone = computed(
        () => auth.user.value?.restaurant?.timezone || "UTC",
      );
      const todayStr = computed(() =>
        getRestaurantDateKey(new Date(), timezone.value),
      );

      const closures = ref<ClosureEntry[]>([]);
      const loading = ref(false);
      const saving = ref(false);
      const newDate = ref("");
      // Default = the original behaviour: close the whole day.
      const allDay = ref(true);
      const newStart = ref("18:00");
      const newEnd = ref("20:00");
      // Optional note shown to guests on the customer booking page for that
      // day (same `guestNote` field the Settings → Vacation form writes).
      const newGuestNote = ref("");

      const rangeInvalid = computed(
        () => !allDay.value && !isValidRange(newStart.value, newEnd.value),
      );

      const showToast = (message: string, type: "success" | "error") =>
        toast.showToast(message, { type, autoHide: 3000 });

      // Read the full vacation_schedule items array from settings.
      const readScheduleItems = async (): Promise<any[]> => {
        const settingsResponse = await api.getSettings();
        const vacationSetting = settingsResponse.data.find(
          (s) => s.key === RestaurantSettingKey.VACATION_SCHEDULE,
        );
        if (!vacationSetting?.value) return [];
        const parsed =
          typeof vacationSetting.value === "string"
            ? JSON.parse(vacationSetting.value)
            : vacationSetting.value;
        // Stored as { items: [...] } but may be a bare array in legacy data.
        return Array.isArray(parsed) ? parsed : parsed?.items || [];
      };

      const writeScheduleItems = async (items: any[]) => {
        await api.bulkUpdateSettings(
          {
            settings: [
              {
                key: RestaurantSettingKey.VACATION_SCHEDULE,
                value: JSON.stringify({ items }),
                value_type: "json",
              },
            ],
          },
          auth,
        );
      };

      const loadClosures = async () => {
        loading.value = true;
        try {
          const items = await readScheduleItems();
          closures.value = items
            .filter(
              (entry: any) =>
                entry.reason === CLOSURE_REASON &&
                (entry.payload?.type === "closed_all_day" ||
                  entry.payload?.type === "closed_range") &&
                entry.date >= todayStr.value,
            )
            .map(
              (entry: any): ClosureEntry => ({
                date: entry.date,
                ...(entry.payload.type === "closed_range"
                  ? { start: entry.payload.start, end: entry.payload.end }
                  : {}),
                guestNote: entry.guestNote,
              }),
            )
            // By date, all-day first, then ranges by start time.
            .sort(
              (a, b) =>
                a.date.localeCompare(b.date) ||
                (a.start ?? "").localeCompare(b.start ?? ""),
            );
        } catch (e) {
          console.error("Failed to load scheduled closures", e);
        } finally {
          loading.value = false;
        }
      };

      const addClosure = async () => {
        const date = newDate.value;
        if (!date || rangeInvalid.value) return;
        if (date < todayStr.value) {
          showToast(t("reservations.closures.pick_future_date"), "error");
          return;
        }
        saving.value = true;
        try {
          const items = await readScheduleItems();
          const closedAllDay = items.some(
            (entry: any) =>
              entry.date === date && entry.payload?.type === "closed_all_day",
          );
          let item: VacationScheduleItem | null = null;
          if (allDay.value) {
            // Don't duplicate an existing closed-all-day entry for that date.
            if (!closedAllDay) {
              item = {
                date,
                reason: CLOSURE_REASON,
                payload: { type: "closed_all_day" },
              };
            }
          } else {
            if (closedAllDay) {
              showToast(
                t("reservations.closures.already_closed_all_day"),
                "error",
              );
              return;
            }
            const entry = { date, start: newStart.value, end: newEnd.value };
            // Several ranges per date are fine; an identical one is skipped.
            if (!items.some((existing: any) => matchesEntry(existing, entry))) {
              item = {
                date,
                reason: CLOSURE_REASON,
                payload: {
                  type: "closed_range",
                  start: newStart.value,
                  end: newEnd.value,
                },
              };
            }
          }
          if (item) {
            const guestNote = newGuestNote.value.trim().slice(0, 500);
            items.push(guestNote ? { ...item, guestNote } : item);
            await writeScheduleItems(items);
          }
          newDate.value = "";
          newGuestNote.value = "";
          await loadClosures();
          showToast(t("reservations.closures.scheduled"), "success");
        } catch (e) {
          console.error("Failed to schedule closure", e);
          showToast(t("reservations.closures.schedule_failed"), "error");
        } finally {
          saving.value = false;
        }
      };

      const removeClosure = async (entry: ClosureEntry) => {
        saving.value = true;
        try {
          const items = await readScheduleItems();
          // Remove only this closure — other ranges on the same date stay.
          const next = items.filter((item: any) => !matchesEntry(item, entry));
          await writeScheduleItems(next);
          await loadClosures();
          showToast(t("reservations.closures.removed"), "success");
        } catch (e) {
          console.error("Failed to remove closure", e);
          showToast(t("reservations.closures.remove_failed"), "error");
        } finally {
          saving.value = false;
        }
      };

      const formatDate = (date: string) => {
        // date is YYYY-MM-DD — render without timezone shifting
        const [y, m, d] = date.split("-").map(Number);
        return new Date(y, m - 1, d).toLocaleDateString(undefined, {
          weekday: "short",
          day: "numeric",
          month: "short",
          year: "numeric",
        });
      };

      const formatRange = (entry: ClosureEntry) =>
        entry.start === undefined
          ? t("reservations.closures.all_day")
          : `${entry.start}–${entry.end}`;

      watch(
        () => props.isOpen,
        (open) => {
          if (open) {
            newDate.value = "";
            newGuestNote.value = "";
            allDay.value = true;
            newStart.value = "18:00";
            newEnd.value = "20:00";
            loadClosures();
          }
        },
        { immediate: true },
      );

      const renderTimeInput = (
        id: string,
        label: string,
        model: typeof newStart,
      ) => (
        <div class="flex-1">
          <label
            for={id}
            class="block text-xs font-medium text-base-content/60 mb-1"
          >
            {label}
          </label>
          <input
            id={id}
            type="time"
            class={`input input-bordered input-sm w-full ${
              rangeInvalid.value ? "input-error" : ""
            }`}
            value={model.value}
            onInput={(e) =>
              (model.value = (e.target as HTMLInputElement).value)
            }
          />
        </div>
      );

      return () => (
        <RightSheet
          isOpen={props.isOpen}
          onClose={props.onClose}
          width="460px"
          title={
            <div class="flex items-center gap-2 text-accent">
              <span class="material-symbols-outlined text-xl text-primary">
                event_busy
              </span>
              <span class="text-lg font-semibold">
                {t("reservations.header.scheduled_closures")}
              </span>
            </div>
          }
        >
          <div class="px-5 py-4 space-y-5">
            <p class="text-sm text-base-content/60 leading-relaxed">
              {t("reservations.closures.description")}
            </p>

            {/* Add a closure */}
            <div class="space-y-3">
              <div class="flex items-end gap-2">
                <div class="flex-1">
                  <label
                    for="closure-date"
                    class="block text-xs font-medium text-base-content/60 mb-1"
                  >
                    {t("reservations.closures.add_date")}
                  </label>
                  <input
                    id="closure-date"
                    type="date"
                    class="input input-bordered input-sm w-full"
                    min={todayStr.value}
                    value={newDate.value}
                    onInput={(e) =>
                      (newDate.value = (e.target as HTMLInputElement).value)
                    }
                  />
                </div>
                <button
                  type="button"
                  class="btn btn-primary btn-sm gap-1"
                  disabled={
                    !newDate.value || rangeInvalid.value || saving.value
                  }
                  onClick={addClosure}
                >
                  <span class="material-symbols-outlined text-base">add</span>
                  {t("reservations.closures.close_action")}
                </button>
              </div>

              <label class="flex items-center gap-3 min-h-8 cursor-pointer w-fit">
                <input
                  type="checkbox"
                  class="toggle toggle-sm toggle-primary"
                  checked={allDay.value}
                  onChange={(e) =>
                    (allDay.value = (e.target as HTMLInputElement).checked)
                  }
                />
                <span class="text-sm text-base-content">
                  {t("reservations.closures.all_day")}
                </span>
              </label>

              {!allDay.value && (
                <div class="space-y-1">
                  <div class="flex items-end gap-2">
                    {renderTimeInput(
                      "closure-start",
                      t("settings.vacation.start"),
                      newStart,
                    )}
                    {renderTimeInput(
                      "closure-end",
                      t("settings.vacation.end"),
                      newEnd,
                    )}
                  </div>
                  {rangeInvalid.value && (
                    <p class="text-xs text-error">
                      {t("reservations.closures.invalid_range")}
                    </p>
                  )}
                </div>
              )}

              <div>
                <label
                  for="closure-guest-note"
                  class="block text-xs font-medium text-base-content/60 mb-1"
                >
                  {t("settings.vacation.guest_note_label")}
                </label>
                <textarea
                  id="closure-guest-note"
                  class="textarea textarea-bordered textarea-sm w-full"
                  rows={2}
                  maxlength={500}
                  placeholder={t("settings.vacation.guest_note_placeholder")}
                  value={newGuestNote.value}
                  onInput={(e) =>
                    (newGuestNote.value = (
                      e.target as HTMLTextAreaElement
                    ).value)
                  }
                />
              </div>
            </div>

            {/* Existing closures */}
            <div class="space-y-2">
              <div class="text-[11px] font-semibold text-base-content/40 uppercase tracking-wider">
                {t("reservations.closures.upcoming")}
              </div>
              {loading.value ? (
                <div class="flex justify-center py-6">
                  <span class="loading loading-spinner loading-md"></span>
                </div>
              ) : closures.value.length === 0 ? (
                <div class="text-sm text-base-content/40 italic py-3">
                  {t("reservations.closures.none")}
                </div>
              ) : (
                <div class="space-y-1.5">
                  {closures.value.map((entry) => (
                    <div
                      key={entryKey(entry)}
                      class="flex items-center gap-2 px-3 py-1 rounded-lg bg-base-200/50 border border-base-300/40"
                    >
                      <span
                        class={`material-symbols-outlined text-base ${
                          entry.start === undefined
                            ? "text-error"
                            : "text-warning"
                        }`}
                        aria-hidden="true"
                      >
                        {entry.start === undefined ? "event_busy" : "schedule"}
                      </span>
                      <div class="flex-1 min-w-0">
                        <div class="text-sm text-accent truncate">
                          {formatDate(entry.date)}
                        </div>
                        {entry.guestNote && (
                          <div class="text-xs text-base-content/50 truncate">
                            {entry.guestNote}
                          </div>
                        )}
                      </div>
                      <span class="w-24 shrink-0 text-right text-sm tabular-nums text-base-content/70">
                        {formatRange(entry)}
                      </span>
                      <button
                        type="button"
                        class="btn btn-ghost btn-sm btn-square shrink-0 text-base-content/50 hover:text-error"
                        disabled={saving.value}
                        title={t("reservations.closures.remove")}
                        aria-label={t("reservations.closures.remove")}
                        onClick={() => removeClosure(entry)}
                      >
                        <span class="material-symbols-outlined text-base">
                          close
                        </span>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </RightSheet>
      );
    },
  });
